import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { FloatingPopupLineChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-line-chart-wrapper/floating-popup-line-chart-wrapper.component';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { FpOpenCustomCorrelationActionComponent } from '@app/main/leak-detection/components/fp-open-custom-correlation-action/fp-open-custom-correlation-action.component';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FpCorrelationSpectrumActionType } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum/fp-correlation-spectrum.actions';
import { FpCorrelationSpectrumStore } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum/fp-correlation-spectrum.store';
import { FpCorrelationSpectrumExportService } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum-export.service';
import { FpCorrelationSpectrumChartService } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/services/fp-correlation-spectrum-chart.service';
import { NgClass } from '@angular/common';
import { FpLoadSoundSignalActionComponent } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-spectrum/components/fp-load-sound-signals-action/fp-load-sound-signals-action.component';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-correlation-spectrum',
  standalone: true,
  imports: [
    ElementWidthClassDirective,
    FpOpenCustomCorrelationActionComponent,
    FloatingPopupLineChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FpLoadSoundSignalActionComponent,
    FloatingPopupBaseErrorMessageComponent,
    NgClass,
  ],
  templateUrl: './fp-correlation-spectrum.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpCorrelationSpectrumComponent {
  protected readonly screenResolutionService = inject(ScreenResolutionService);
  public readonly currentTabStore = inject(FpCorrelationSpectrumStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpCorrelationSpectrumChartService);
  protected readonly fpCorrelationSpectrumExportService = inject(FpCorrelationSpectrumExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);

  public popup = input<IFloatingPopupInstance>();

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly correlation = computed(() => this.popupMainData()?.correlation);
  public readonly loggerDeployment1 = computed(() => this.popupMainData()?.loggerDeployment1);
  public readonly loggerDeployment2 = computed(() => this.popupMainData()?.loggerDeployment2);

  public combinedChartData = computed(() => {
    const data = this.data() || [];

    const chartData = this.chartService.getDataForChart(data);
    const chartOptions = this.chartService.generateOptions(data);

    return { chartData, chartOptions };
  });

  public doExport() {
    const popupMainData = this.popupMainData();

    if (!popupMainData) {
      return;
    }

    const data = this.data() || [];

    this.fpCorrelationSpectrumExportService.exportCsv(popupMainData, data);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpCorrelationSpectrumActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
